import React, { useState, useEffect } from 'react';
import { X, Info, AlertTriangle, Wrench, Sparkles, AlertCircle } from 'lucide-react';
import { getActiveAnnouncements, dismissAnnouncement, markAnnouncementAsViewed } from '../services/announcementService';

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const data = await getActiveAnnouncements();
      setAnnouncements(data);
      
      // Mark all announcements as viewed
      data.forEach(announcement => {
        if (!announcement.hasViewed) {
          markAnnouncementAsViewed(announcement._id).catch(console.error);
        }
      });
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (announcementId) => {
    try {
      await dismissAnnouncement(announcementId);
      setAnnouncements(prev => prev.filter(a => a._id !== announcementId));
    } catch (error) {
      console.error('Error dismissing announcement:', error);
      alert('Failed to dismiss announcement. Please try again.');
    }
  };

  const getAnnouncementStyles = (type, priority) => {
    const baseStyles = "rounded-lg border-l-4 shadow-sm";
    
    let colorStyles = '';
    switch (type) {
      case 'important':
        colorStyles = 'bg-red-50 border-red-500 text-red-900';
        break;
      case 'maintenance':
        colorStyles = 'bg-amber-50 border-amber-500 text-amber-900';
        break;
      case 'update':
        colorStyles = 'bg-blue-50 border-blue-500 text-blue-900';
        break;
      case 'feature':
        colorStyles = 'bg-green-50 border-green-500 text-green-900';
        break;
      default:
        colorStyles = 'bg-indigo-50 border-indigo-500 text-indigo-900';
    }
    
    return `${baseStyles} ${colorStyles}`;
  };

  const getIcon = (type) => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    
    switch (type) {
      case 'important':
        return <AlertCircle className={iconClass} />;
      case 'maintenance':
        return <Wrench className={iconClass} />;
      case 'update':
        return <AlertTriangle className={iconClass} />;
      case 'feature':
        return <Sparkles className={iconClass} />;
      default:
        return <Info className={iconClass} />;
    }
  };

  if (loading) {
    return null;
  }

  if (announcements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {announcements.map((announcement) => (
        <div
          key={announcement._id}
          className={getAnnouncementStyles(announcement.type, announcement.priority)}
        >
          <div className="p-4 flex items-start gap-3">
            {getIcon(announcement.type)}
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm mb-1">
                {announcement.title}
              </h3>
              <p className="text-sm whitespace-pre-line">
                {announcement.message}
              </p>
              {announcement.publishedAt && (
                <p className="text-xs mt-2 opacity-70">
                  Posted {new Date(announcement.publishedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
            
            {announcement.isDismissible && (
              <button
                onClick={() => handleDismiss(announcement._id)}
                className="flex-shrink-0 p-1 rounded-full hover:bg-black/10 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
